import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { fetchEventSource } from '@microsoft/fetch-event-source';

interface LeadFormProps {
  data?: any; // Not used, but kept for consistency with other custom elements
  apiBaseUrl: string;
  sessionId: string;
  sessionKey: string;
  /** Callback for SSE events - routes to ChatEngine.handleSseEvent */
  onSseEvent: (event: { event: string; data: string }) => void;
}

const hasStoredSubmission = (storageKey: string | null): boolean => {
  if (!storageKey || typeof window === 'undefined') return false;

  try {
    return window.localStorage.getItem(storageKey) === 'true';
  } catch {
    return false;
  }
};

const storeSubmission = (storageKey: string | null): void => {
  if (!storageKey || typeof window === 'undefined') return;

  try {
    window.localStorage.setItem(storageKey, 'true');
  } catch {
    // The backend remains the source of truth when browser storage is unavailable.
  }
};

const clearStoredSubmission = (storageKey: string | null): void => {
  if (!storageKey || typeof window === 'undefined') return;

  try {
    window.localStorage.removeItem(storageKey);
  } catch {
    // Editing still works for the current page when browser storage is unavailable.
  }
};

export const LeadForm: React.FC<LeadFormProps> = ({
  apiBaseUrl,
  sessionId,
  sessionKey,
  onSseEvent,
}) => {
  const { t } = useTranslation();
  const submissionStorageKey = sessionId
    ? `travel-assistant-lead-submitted:${sessionId}`
    : null;
  const [isSubmitted, setIsSubmitted] = useState(() =>
    hasStoredSubmission(submissionStorageKey));
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [email, setEmail] = useState('');
  const [contactPreference, setContactPreference] = useState<'Phone' | 'Email'>('Phone');
  const [bestTimeToContact, setBestTimeToContact] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submissionInFlight = useRef(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const validatePhone = (phone: string): boolean => {
    // E.164 format validation: +[country code][number]
    const e164Regex = /^\+[1-9]\d{1,14}$/;
    return e164Regex.test(phone);
  };

  const validateEmail = (emailValue: string): boolean => {
    // Basic email format validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(emailValue);
  };

  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (!firstName.trim()) {
      newErrors.firstName = t('leadForm.validation.firstNameRequired');
    }

    if (!lastName.trim()) {
      newErrors.lastName = t('leadForm.validation.lastNameRequired');
    }

    if (!phoneNumber.trim()) {
      newErrors.phoneNumber = t('leadForm.validation.phoneRequired');
    } else if (!validatePhone(phoneNumber)) {
      newErrors.phoneNumber = t('leadForm.validation.phoneInvalid');
    }

    if (!email.trim()) {
      newErrors.email = t('leadForm.validation.emailRequired');
    } else if (!validateEmail(email)) {
      newErrors.email = t('leadForm.validation.emailInvalid');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (submissionInFlight.current) {
      return;
    }

    if (!validateForm()) {
      return;
    }

    if (!sessionId || !sessionKey) {
      setErrors({ submit: t('leadForm.validation.sessionUnavailable') });
      return;
    }

    submissionInFlight.current = true;
    setIsSubmitting(true);
    setErrors({});

    try {
      // Call API to store lead information and stream AI response via SSE
      await fetchEventSource(`${apiBaseUrl}/v1/sessions/${sessionId}/lead`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          sessionKey,
          firstName,
          lastName,
          phoneNumber,
          email,
          contactPreference,
          bestTimeToContact: bestTimeToContact || null,
        }),
        onmessage: (event) => {
          // Route SSE events to the chat engine
          onSseEvent({ event: event.event ?? '', data: event.data });
        },
        onerror: (err) => {
          console.error('SSE error during lead submission:', err);
          throw err;
        },
      });

      storeSubmission(submissionStorageKey);
      setIsSubmitted(true);
    } catch (error) {
      console.error('Failed to submit lead info:', error);
      setErrors({
        submit: error instanceof Error ? error.message : t('leadForm.validation.submitError')
      });
    } finally {
      submissionInFlight.current = false;
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="lead-form-container">
        <div className="lead-form-card" role="status">
          <h3 className="lead-form-title">{t('leadForm.success.title')}</h3>
          <p className="lead-form-description">
            {t('leadForm.success.description')}
          </p>
          <button
            type="button"
            className="submit-button"
            onClick={() => {
              clearStoredSubmission(submissionStorageKey);
              setIsSubmitted(false);
            }}
          >
            {t('leadForm.buttons.edit')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="lead-form-container">
      <div className="lead-form-card">
        <h3 className="lead-form-title">{t('leadForm.title')}</h3>
        <p className="lead-form-description">
          {t('leadForm.description')}
        </p>

        <form onSubmit={handleSubmit} className="lead-form">
          <div className="form-group">
            <label htmlFor="firstName" className="form-label">
              {t('leadForm.labels.firstName')} <span className="required">*</span>
            </label>
            <input
              type="text"
              id="firstName"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              className={`form-input ${errors.firstName ? 'error' : ''}`}
              placeholder={t('leadForm.placeholders.firstName')}
            />
            {errors.firstName && (
              <span className="error-message">{errors.firstName}</span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="lastName" className="form-label">
              {t('leadForm.labels.lastName')} <span className="required">*</span>
            </label>
            <input
              type="text"
              id="lastName"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              className={`form-input ${errors.lastName ? 'error' : ''}`}
              placeholder={t('leadForm.placeholders.lastName')}
            />
            {errors.lastName && (
              <span className="error-message">{errors.lastName}</span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="phoneNumber" className="form-label">
              {t('leadForm.labels.phoneNumber')} <span className="required">*</span>
            </label>
            <input
              type="tel"
              id="phoneNumber"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              className={`form-input ${errors.phoneNumber ? 'error' : ''}`}
              placeholder={t('leadForm.placeholders.phoneNumber')}
            />
            <span className="form-hint">{t('leadForm.hints.phoneCountryCode')}</span>
            {errors.phoneNumber && (
              <span className="error-message">{errors.phoneNumber}</span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="email" className="form-label">
              {t('leadForm.labels.email')} <span className="required">*</span>
            </label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={`form-input ${errors.email ? 'error' : ''}`}
              placeholder={t('leadForm.placeholders.email')}
            />
            <span className="form-hint">{t('leadForm.hints.emailQuote')}</span>
            {errors.email && (
              <span className="error-message">{errors.email}</span>
            )}
          </div>

          <div className="form-group">
            <label className="form-label">
              {t('leadForm.labels.contactPreference')} <span className="required">*</span>
            </label>
            <div className="radio-group">
              <label className="radio-label">
                <input
                  type="radio"
                  name="contactPreference"
                  value="Phone"
                  checked={contactPreference === 'Phone'}
                  onChange={(e) => setContactPreference(e.target.value as 'Phone' | 'Email')}
                  className="radio-input"
                />
                <span>{t('leadForm.contactOptions.phone')}</span>
              </label>
              <label className="radio-label">
                <input
                  type="radio"
                  name="contactPreference"
                  value="Email"
                  checked={contactPreference === 'Email'}
                  onChange={(e) => setContactPreference(e.target.value as 'Phone' | 'Email')}
                  className="radio-input"
                />
                <span>{t('leadForm.contactOptions.email')}</span>
              </label>
            </div>
          </div>

          {contactPreference === 'Phone' && (
            <div className="form-group">
              <label htmlFor="bestTimeToContact" className="form-label">
                {t('leadForm.labels.bestTimeToContact')}
              </label>
              <select
                id="bestTimeToContact"
                value={bestTimeToContact}
                onChange={(e) => setBestTimeToContact(e.target.value)}
                className="form-input"
              >
                <option value="">{t('leadForm.placeholders.selectTime')}</option>
                <option value="Morning (9AM-12PM)">{t('leadForm.timeSlots.morning')}</option>
                <option value="Afternoon (12PM-5PM)">{t('leadForm.timeSlots.afternoon')}</option>
                <option value="Evening (5PM-9PM)">{t('leadForm.timeSlots.evening')}</option>
              </select>
            </div>
          )}

          {errors.submit && (
            <div className="submit-error">{errors.submit}</div>
          )}

          <button type="submit" className="submit-button" disabled={isSubmitting}>
            {isSubmitting ? t('leadForm.buttons.submitting') : t('leadForm.buttons.continue')}
          </button>
        </form>
      </div>
    </div>
  );
};
